'use client'

import { datadogRum } from '#rum-sdk'
import {
  type BrowserMetricTransport,
  createBrowserMetricTransport,
} from './direct-metrics-browser'
import {
  installBrowserLifecycleDrain,
  registerBrowserLifecycleDrain,
} from './lifecycle-browser'
import {
  buildMetricObservation,
  classifyMetricError,
  createMetricRecorder,
  defineMetric,
  installMetricSink,
  isMetricOutcome,
  type MetricDefinition,
  type MetricOptions,
  type MetricOutcome,
  metricNow,
} from './metrics'
import { areRumVitalsEnabled } from './rum-runtime'
import { incrementTelemetryHealth } from './telemetry-health'

const JOURNEY_TAGS = [
  'operation',
  'domain',
  'runtime',
  'outcome',
  'reason',
  'surface',
  'phase',
  'function',
  'cache_state',
  'transport',
  'navigation_type',
  'engine',
  'device_class',
  'sample_policy',
  'resource',
  'trigger',
] as const

export const CLIENT_METRICS = {
  duration: defineMetric({
    name: 'brew.journey.duration',
    type: 'distribution',
    unit: 'millisecond',
    tags: JOURNEY_TAGS,
  }),
  completed: defineMetric({
    name: 'brew.journey.completed',
    type: 'count',
    unit: 'count',
    tags: JOURNEY_TAGS,
  }),
  renderDuration: defineMetric({
    name: 'brew.render.window.duration',
    type: 'distribution',
    unit: 'millisecond',
    tags: JOURNEY_TAGS,
  }),
  renderCount: defineMetric({
    name: 'brew.render.window.samples',
    type: 'count',
    unit: 'count',
    tags: JOURNEY_TAGS,
  }),
  renderPhase: defineMetric({
    name: 'brew.render.phase.duration',
    type: 'distribution',
    unit: 'millisecond',
    tags: JOURNEY_TAGS,
  }),
  dropped: defineMetric({
    name: 'brew.telemetry.client.dropped',
    type: 'count',
    unit: 'count',
    tags: ['runtime', 'reason', 'surface'],
  }),
} as const

export type ClientJourneyOutcome = MetricOutcome
export type ClientJourney = {
  finish: (
    outcome: ClientJourneyOutcome,
    options?: MetricOptions & { completedAt?: number }
  ) => void
}

let enabled = false
let removeSink: (() => void) | undefined
let removeTransportDrain: (() => void) | undefined
let transport: BrowserMetricTransport | undefined
const activeJourneys = new Set<ClientJourney>()
const MAX_ACTIVE_JOURNEYS = 256
let droppedJourneys = 0
const noopJourney: ClientJourney = { finish() {} }

const emit = createMetricRecorder((observation) => {
  if (!transport?.enqueue(observation)) {
    throw new Error('native browser metric rejected')
  }
})

/** Optional collectors must check this before allocating browser resources. */
export function isClientMetricsEnabled(): boolean {
  return enabled
}

/** Drain completed aggregate windows before the SDK's hidden/freeze batch. */
export function onClientMetricsDrain(drain: () => void): () => void {
  return registerBrowserLifecycleDrain(() => {
    if (enabled) {
      drain()
    }
  })
}

/** Native measurements are independent of RUM credentials and session sampling. */
export function configureClientMetrics(
  value: boolean,
  options: {
    transport?: BrowserMetricTransport
  } = {}
): void {
  if (enabled === value && !options.transport) {
    return
  }
  enabled = value
  try {
    removeSink?.()
    removeSink = undefined
    removeTransportDrain?.()
    removeTransportDrain = undefined
    transport?.dispose()
    transport = undefined
    if (value) {
      transport = options.transport ?? createBrowserMetricTransport()
      installBrowserLifecycleDrain()
      removeSink = installMetricSink((observation) => {
        if (enabled && !transport?.enqueue(observation)) {
          throw new Error('native browser metric rejected')
        }
      })
      removeTransportDrain = registerBrowserLifecycleDrain(
        flushClientMetricsOnExit
      )
      if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', abandonClientJourneys)
        window.addEventListener('pagehide', flushClientMetricsOnExit)
      }
    } else {
      // Explicit disablement discards outstanding observations, including IDs.
      incrementTelemetryHealth('client_journey_discarded', activeJourneys.size)
      abandonClientJourneys()
      droppedJourneys = 0
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', abandonClientJourneys)
        window.removeEventListener('pagehide', flushClientMetricsOnExit)
      }
    }
  } catch {
    incrementTelemetryHealth('client_lifecycle_failed')
    if (!transport) {
      enabled = false
    }
    // Browser lifecycle hooks are optional; collectors cannot break bootstrap.
  }
}

function flushClientMetricsOnExit(): void {
  // Let all synchronous aggregate drains and abandoned journeys enqueue first.
  queueMicrotask(() => {
    void transport?.flush(true)
  })
}

function abandonClientJourneys(): void {
  for (const journey of activeJourneys) {
    journey.finish('cancelled', { tags: { reason: 'page_hidden_or_shutdown' } })
  }
}

export function recordClientMetric(
  definition: MetricDefinition,
  value: number,
  options: MetricOptions = {}
): boolean {
  if (!enabled) {
    incrementTelemetryHealth('client_disabled')
    return false
  }
  try {
    return emit(definition, value, {
      ...options,
      tags: { ...options.tags, runtime: 'browser' },
    })
  } catch {
    incrementTelemetryHealth('metric_invalid_metadata')
    return false
  }
}

/**
 * Direct native points are authoritative. The matching
 * RUM vital is for view/trace investigation only; counting both duplicates data.
 * startTime must be UNIX milliseconds; duration is monotonic milliseconds.
 */
export function recordClientDuration(
  operation: string,
  startTime: number,
  duration: number,
  outcome: ClientJourneyOutcome,
  options: MetricOptions = {}
): void {
  if (!enabled) {
    return
  }
  if (
    !Number.isFinite(startTime) ||
    !Number.isFinite(duration) ||
    duration < 0
  ) {
    incrementTelemetryHealth('metric_invalid_value')
    return
  }
  try {
    const observationOptions = {
      ...options,
      tags: { ...options.tags, runtime: 'browser', operation, outcome },
    }
    recordClientMetric(CLIENT_METRICS.duration, duration, observationOptions)
    recordClientMetric(CLIENT_METRICS.completed, 1, observationOptions)
    // Native points do not depend on RUM. Avoid its optional envelope and SDK
    // pre-init queue unless our bootstrap enabled session collection.
    if (!areRumVitalsEnabled()) {
      return
    }
    const observation = buildMetricObservation(
      CLIENT_METRICS.duration,
      duration,
      observationOptions
    )
    if (
      observation?.metric.tags.operation === operation &&
      typeof datadogRum.addDurationVital === 'function'
    ) {
      try {
        datadogRum.addDurationVital(operation, {
          startTime,
          duration,
          context: observation,
        })
      } catch {
        incrementTelemetryHealth('client_vital_failed')
      }
    } else if (observation?.metric.tags.operation === operation) {
      incrementTelemetryHealth('client_vital_failed')
    }
  } catch {
    incrementTelemetryHealth('metric_invalid_metadata')
    // Optional SDK features and collector failures cannot affect the product.
  }
}

/**
 * One handle per actual operation, even when names overlap. Call finish from
 * the product's completion/abort path; cleanup and the bounded deadline cover
 * abandoned operations without holding SDK vital state or blocking a caller.
 */
export function startClientJourney(
  operation: string,
  options: MetricOptions & { timeoutMs?: number } = {}
): ClientJourney {
  if (!enabled) {
    incrementTelemetryHealth('client_disabled')
    return noopJourney
  }
  if (activeJourneys.size >= MAX_ACTIVE_JOURNEYS) {
    droppedJourneys += 1
    incrementTelemetryHealth('client_journey_overflow')
    return noopJourney
  }
  try {
    const start = metricNow()
    const startTime = Date.now()
    const initialTags = { ...options.tags }
    const initialContext = { ...options.context }
    let finished = false
    let timeout: ReturnType<typeof setTimeout>
    const handle: ClientJourney = {
      finish(outcome, completion = {}) {
        if (finished) {
          return
        }
        finished = true
        activeJourneys.delete(handle)
        try {
          const completedAt = completion.completedAt ?? metricNow()
          if (droppedJourneys > 0) {
            recordClientMetric(CLIENT_METRICS.dropped, droppedJourneys, {
              tags: { reason: 'active_journey_capacity', surface: 'journey' },
            })
            droppedJourneys = 0
          }
          clearTimeout(timeout)
          recordClientDuration(
            operation,
            startTime,
            Math.max(0, completedAt - start),
            outcome,
            {
              tags: { ...initialTags, ...completion.tags },
              context: { ...initialContext, ...completion.context },
            }
          )
        } catch {
          incrementTelemetryHealth('client_lifecycle_failed')
          // Completion must remain idempotent even if the browser is shutting down.
        }
      },
    }
    const timeoutMs = Number.isFinite(options.timeoutMs)
      ? Math.min(600_000, Math.max(1, options.timeoutMs ?? 120_000))
      : 120_000
    timeout = setTimeout(
      () => handle.finish('timeout', { tags: { reason: 'deadline' } }),
      timeoutMs
    )
    activeJourneys.add(handle)
    return handle
  } catch {
    incrementTelemetryHealth('client_lifecycle_failed')
    return noopJourney
  }
}

/** Observe a client-owned request/save without changing its result or exception. */
export async function observeClientOperation<T>(
  operation: string,
  options: MetricOptions & {
    timeoutMs?: number
    classifyResult?: (result: T) => ClientJourneyOutcome
  },
  callback: () => Promise<T>
): Promise<T> {
  const journey = startClientJourney(operation, options)
  try {
    const result = await callback()
    const completedAt = metricNow()
    let outcome: ClientJourneyOutcome = 'success'
    try {
      if (options.classifyResult) {
        const classified = options.classifyResult(result)
        outcome = isMetricOutcome(classified) ? classified : 'unknown'
      }
    } catch {
      // Classifier failure says nothing about the business outcome.
      outcome = 'unknown'
    }
    journey.finish(outcome, { completedAt })
    return result
  } catch (error) {
    const completedAt = metricNow()
    journey.finish(classifyMetricError(error), { completedAt })
    throw error
  }
}
